import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useAuthStore from '../hooks/useAuthStore';
import { useDropzone } from 'react-dropzone';

export default function UploadHub() {
  const navigate = useNavigate();
  const { user, theme } = useAuthStore();
  const isICSE = theme === 'icse';
  const primaryColor = isICSE ? 'icse-teal' : 'isc-coral';
  const secondaryColor = isICSE ? 'icse-cyan' : 'isc-purple';

  const [uploadData, setUploadData] = useState({
    class: user?.stream || 'ICSE Class 9',
    subject: '',
    chapter: '',
    description: '',
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'History', 'Geography', 'Computer Applications'];
  const chapters = Array.from({ length: 20 }, (_, i) => `Chapter ${i + 1}`);

  const onDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(
      (file) => file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'application/pdf'
    );

    if (validFiles.length !== acceptedFiles.length) {
      alert('Some files were rejected. Only JPG, PNG, and PDF files are allowed.');
    }

    setUploadedFiles((prev) => [...prev, ...validFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/pdf': ['.pdf'],
    },
    maxSize: 10 * 1024 * 1024,
  });

  const handleRemoveFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (uploadedFiles.length === 0) {
      alert('Please select at least one file to upload');
      return;
    }

    if (!uploadData.subject || !uploadData.chapter) {
      alert('Please select subject and chapter');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      uploadedFiles.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('class', uploadData.class);
      formData.append('subject', uploadData.subject);
      formData.append('chapter', uploadData.chapter);
      formData.append('description', uploadData.description);
      formData.append('userId', user.id);
      formData.append('userName', user.name);

      // Since we don't have a backend, simulate upload success
      // In production, this would call: const response = await fetch('/api/upload', { method: 'POST', body: formData });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      setUploadSuccess(true);
      setUploadedFiles([]);
      setUploadData({
        class: user?.stream || 'ICSE Class 9',
        subject: '',
        chapter: '',
        description: '',
      });

      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Upload error:', error);
      alert('An error occurred during upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleChange = (e) => {
    setUploadData({ ...uploadData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Upload Hub</h1>
              <p className={`text-${primaryColor}-400 text-sm`}>Share your notes with the community</p>
            </div>
          </div>

          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Back to Portal
          </button>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6">Upload Your Content</h2>

              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                  isDragActive
                    ? `border-${primaryColor}-500 bg-${primaryColor}-500/10`
                    : 'border-gray-600 hover:border-gray-500 bg-white/5'
                }`}
              >
                <input {...getInputProps()} />
                <div className="text-5xl mb-4">📤</div>
                <p className="text-white font-medium mb-2">
                  {isDragActive ? 'Drop your files here' : 'Drag & drop files here'}
                </p>
                <p className="text-gray-400 text-sm mb-4">or click to browse</p>
                <p className="text-gray-500 text-xs">
                  Accepted formats: JPG, PNG, PDF (Max 10MB per file)
                </p>
              </div>

              {uploadedFiles.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 space-y-3"
                >
                  <h3 className="text-white font-medium">Selected Files</h3>
                  {uploadedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10"
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-2xl">
                          {file.type === 'application/pdf' ? '📄' : '🖼️'}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{file.name}</p>
                          <p className="text-gray-400 text-xs">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </motion.div>
              )}
            </div>

            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6">Content Details</h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Class</label>
                  <select
                    name="class"
                    value={uploadData.class}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-${primaryColor}-500 focus:ring-1 focus:ring-${primaryColor}-500 transition-all"
                  >
                    <option value="ICSE Class 9" className="bg-gray-900">ICSE Class 9</option>
                    <option value="ICSE Class 10" className="bg-gray-900">ICSE Class 10</option>
                    <option value="ISC Class 11 Science" className="bg-gray-900">ISC Class 11 Science</option>
                    <option value="ISC Class 11 Commerce" className="bg-gray-900">ISC Class 11 Commerce</option>
                    <option value="ISC Class 12 Science" className="bg-gray-900">ISC Class 12 Science</option>
                    <option value="ISC Class 12 Commerce" className="bg-gray-900">ISC Class 12 Commerce</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                  <select
                    name="subject"
                    value={uploadData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-${primaryColor}-500 focus:ring-1 focus:ring-${primaryColor}-500 transition-all"
                  >
                    <option value="" className="bg-gray-900">Select a subject</option>
                    {subjects.map((subject) => (
                      <option key={subject} value={subject} className="bg-gray-900">
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Chapter</label>
                  <select
                    name="chapter"
                    value={uploadData.chapter}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-${primaryColor}-500 focus:ring-1 focus:ring-${primaryColor}-500 transition-all"
                  >
                    <option value="" className="bg-gray-900">Select a chapter</option>
                    {chapters.map((chapter) => (
                      <option key={chapter} value={chapter} className="bg-gray-900">
                        {chapter}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description (Optional)</label>
                  <textarea
                    name="description"
                    value={uploadData.description}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Add a brief description of your content..."
                    className="w-full px-4 py-3 bg-white/10 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-${primaryColor}-500 focus:ring-1 focus:ring-${primaryColor}-500 transition-all resize-none"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleUpload}
              disabled={uploading || uploadedFiles.length === 0}
              className={`w-full py-4 bg-gradient-to-r from-${primaryColor}-500 to-${secondaryColor}-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]`}
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Uploading... {uploadProgress}%
                </span>
              ) : (
                'Upload Content'
              )}
            </button>

            {uploadSuccess && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-2xl p-6 border border-green-500/30 bg-green-500/10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Upload Successful!</h3>
                    <p className="text-gray-300 text-sm">Your content has been shared with the community.</p>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
